import { lazy, Suspense } from "react";
import { motion } from "framer-motion";
import { TEST_IDS } from "@/constants/testIds";
import { useT } from "@/i18n/LanguageContext";

const WireframeDiamondCanvas = lazy(() =>
  import("@/components/three/WireframeDiamondCanvas").then((m) => ({
    default: m.WireframeDiamondCanvas,
  })),
);

export const Sourcing = () => {
  const { t } = useT();
  const stages = t("sourcing.stages");

  return (
    <section
      id="process"
      data-testid={TEST_IDS.sourcing.root}
      className="relative py-28 md:py-44 overflow-hidden bg-soft"
    >
      <div className="absolute inset-0 blueprint-bg opacity-50 mask-fade-edges" />

      <div className="container-luxe relative">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="font-mono text-[11px] tracking-[0.4em] text-gold">
              {t("sourcing.index")}
            </span>
            <div className="h-px w-12 bg-gold" />
            <span className="overline">{t("sourcing.overline")}</span>
          </div>
          <h2
            data-testid={TEST_IDS.sourcing.heading}
            className="h-display text-4xl md:text-5xl lg:text-6xl text-ink text-balance"
          >
            {t("sourcing.title1")}
            <br />
            <span className="italic">{t("sourcing.title2")}</span>
          </h2>
          <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-muted_text font-light">
            {t("sourcing.body")}
          </p>
        </motion.div>

        <div className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* 3D wireframe diamond — cursor controls rotation */}
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7 relative"
          >
            <div className="relative w-full aspect-square max-w-[640px] mx-auto">
              <div className="absolute inset-0 m-auto w-3/4 h-3/4 rounded-full bg-ice_blue/25 blur-3xl opacity-70 pointer-events-none" />

              <svg
                viewBox="0 0 400 400"
                className="absolute inset-0 w-full h-full pointer-events-none"
                aria-hidden="true"
              >
                <circle
                  cx="200"
                  cy="200"
                  r="180"
                  fill="none"
                  stroke="#92B4D2"
                  strokeWidth="0.5"
                  strokeDasharray="4 6"
                  opacity="0.5"
                />
                <circle
                  cx="200"
                  cy="200"
                  r="140"
                  fill="none"
                  stroke="#92B4D2"
                  strokeWidth="0.4"
                  strokeDasharray="2 4"
                  opacity="0.4"
                />
                <line
                  x1="0"
                  y1="200"
                  x2="400"
                  y2="200"
                  stroke="#92B4D2"
                  strokeWidth="0.3"
                  strokeDasharray="2 8"
                  opacity="0.3"
                />
                <line
                  x1="200"
                  y1="0"
                  x2="200"
                  y2="400"
                  stroke="#92B4D2"
                  strokeWidth="0.3"
                  strokeDasharray="2 8"
                  opacity="0.3"
                />
                <g
                  fill="#4A4A52"
                  fontFamily="JetBrains Mono, monospace"
                  fontSize="7"
                >
                  <text x="200" y="62" textAnchor="middle">
                    57 FACETS
                  </text>
                  <text x="320" y="200" textAnchor="middle">
                    Ø 6.5MM
                  </text>
                  <text x="200" y="362" textAnchor="middle">
                    PAVILION 41.0°
                  </text>
                  <text x="80" y="200" textAnchor="middle">
                    CROWN 34.5°
                  </text>
                </g>
              </svg>

              <div
                data-testid={TEST_IDS.sourcing.canvas}
                className="absolute inset-0 cursor-grab active:cursor-grabbing"
                aria-label="Diamond sourcing and certification process from selection to private presentation"
              >
                <Suspense fallback={null}>
                  <WireframeDiamondCanvas />
                </Suspense>
              </div>

              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 overline-soft text-[10px] whitespace-nowrap">
                {t("sourcing.rotateHint")}
              </div>
            </div>
          </motion.div>

          {/* Stage list */}
          <div className="lg:col-span-5 relative">
            <ol className="space-y-10">
              {stages.map((s, i) => (
                <motion.li
                  key={s.num}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{
                    duration: 1,
                    ease: [0.22, 1, 0.36, 1],
                    delay: i * 0.1,
                  }}
                  className="grid grid-cols-[3.4rem_1fr] gap-4 pb-10 border-b border-ink/10 last:border-0"
                >
                  <span className="font-display italic text-3xl text-gold pt-1">
                    {s.num}
                  </span>
                  <div>
                    <h4 className="font-display text-xl md:text-2xl text-ink">
                      {s.label}
                    </h4>
                    <p className="mt-2 text-sm leading-relaxed text-muted_text font-light">
                      {s.detail}
                    </p>
                  </div>
                </motion.li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
};
